import React, { useEffect, useState } from "react";
import AppShell, { PageContainer, PageHeader } from "@/components/AppShell";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Users, Clock, Activity, Trophy } from "lucide-react";

export default function TrendsPage() {
  const { t } = useT();
  const [trends, setTrends] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/trends").then((r) => {
      setTrends(r.data.items);
      setActive(r.data.items[0] || null);
    });
  }, []);

  return (
    <AppShell>
      <PageContainer>
        <PageHeader
          kicker={t("page.trends.kicker")}
          title={t("page.trends.title")}
          subtitle={t("page.trends.sub")}
        />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <aside className="lg:col-span-4">
            <div className="rounded-2xl border border-white/5 bg-zinc-900/50 divide-y divide-white/5 overflow-hidden max-h-[560px] overflow-y-auto">
              {trends.map((tr) => (
                <button
                  key={tr.country}
                  onClick={() => setActive(tr)}
                  data-testid={`trend-${tr.country}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors min-h-[56px] ${
                    active?.country === tr.country ? "bg-neon/10" : "hover:bg-white/5"
                  }`}
                >
                  <span className="text-2xl">{tr.flag}</span>
                  <div className="flex-1">
                    <div className="font-display font-bold">{tr.country}</div>
                    <div className="text-xs text-zinc-500">
                      {tr.active_players_k.toLocaleString()}k {t("page.trends.online_k")} · {tr.avg_ping_ms}ms
                    </div>
                  </div>
                  {active?.country === tr.country && (
                    <span className="w-1.5 h-8 bg-neon rounded-full shadow-neon" />
                  )}
                </button>
              ))}
            </div>
          </aside>

          <section className="lg:col-span-8">
            {active ? (
              <article
                key={active.country}
                className="rounded-3xl border border-white/5 bg-zinc-900/50 p-6 sm:p-8 animate-fade-up"
                data-testid="trend-detail"
              >
                <div className="flex items-center gap-4">
                  <span className="text-5xl">{active.flag}</span>
                  <div>
                    <h2 className="font-display text-3xl font-black">{active.country}</h2>
                    <p className="text-zinc-400 text-sm">{t("page.trends.snapshot")}</p>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <StatTile
                    label={t("page.trends.stat_online")}
                    value={`${active.active_players_k.toLocaleString()}k`}
                    icon={Users}
                  />
                  <StatTile label={t("page.trends.stat_peak")} value={active.peak_hours} icon={Clock} />
                  <StatTile label={t("page.trends.stat_ping")} value={`${active.avg_ping_ms} ms`} icon={Activity} />
                  <StatTile label={t("page.trends.stat_tourneys")} value={active.tournaments_this_week} icon={Trophy} />
                </div>

                <div className="mt-8">
                  <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono mb-3">
                    {t("page.trends.top_games")}
                  </div>
                  <ol className="space-y-2">
                    {active.top_games.map((g, i) => (
                      <li
                        key={g}
                        className="flex items-center gap-3 rounded-xl border border-white/5 bg-zinc-950/60 px-4 py-3"
                      >
                        <span className="font-mono text-xs text-neon w-8">#{i + 1}</span>
                        <span className="font-display font-semibold">{g}</span>
                        <div className="ml-auto h-1.5 bg-white/5 rounded-full w-40 overflow-hidden">
                          <div className="h-full bg-neon" style={{ width: `${100 - i * 15}%` }} />
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </article>
            ) : (
              <div className="text-center text-zinc-500 py-20">Select a country.</div>
            )}
          </section>
        </div>
      </PageContainer>
    </AppShell>
  );
}

function StatTile({ label, value, icon: Icon }) {
  return (
    <div className="rounded-2xl border border-white/5 bg-zinc-950/60 p-4">
      <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-500 font-mono">
        <Icon size={12} className="text-neon" />
        {label}
      </div>
      <div className="mt-1 font-display text-xl font-black">{value}</div>
    </div>
  );
}
